import { AlertButton } from "@/Components/NextBase/AlertButton";
import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { LimeDateRangePicker } from "@/Components/NextBase/LimeDateRangePicker";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimePagination } from "@/Components/NextBase/LimePagination";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { Calendar } from "@/Components/ui/calendar";
import { api } from "@/lib/api-client";
import { GetLimeAdminShifts } from "@/server-types";
import {
  addToast,
  Button,
  Divider,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  RangeValue,
  Selection,
  Spinner,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure,
} from "@heroui/react";
import { parseAbsoluteToLocal, ZonedDateTime } from "@internationalized/date";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import { Bomb, Search, Trash2 } from "lucide-react";
import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

export const ShiftsTab = ({ clientId }: { clientId: number }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get("page");
  const perPageParam = searchParams.get("perPage");
  const totalPagesParam = searchParams.get("totalPages");

  const page = pageParam ? Number(pageParam) : 1;
  const perPage = perPageParam ? Number(perPageParam) : 10;
  const totalPages = totalPagesParam ? Number(totalPagesParam) : 1;

  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const [selectedLocationId, setSelectedLocationId] = useState<
    number | undefined
  >(undefined);

  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(
    undefined,
  );

  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set());

  const {
    data: shifts,
    isFetching,
    refetch,
  } = api.admin.useGetShifts({
    clientId,
    userId: selectedUserId,
    locationId: selectedLocationId,
    page,
    perPage,
  });

  const { mutateAsync: deleteShifts, isPending: isDeleting } =
    api.admin.useDeleteShifts();

  const { data: locationsData, isFetching: isFetchingLocations } =
    api.location.useGetLocations({
      clientId,
      page: 1,
      perPage: 10,
      sortBy: "labelAsc",
    });

  const { data: usersData, isFetching: isFetchingUsers } = api.user.useUserList(
    {
      clientId,
      includeDeleted: true,
    },
  );

  /**
   * Update total pages in URL when data changes
   */
  useEffect(() => {
    if (shifts?.pageCount != null) {
      const newTotalPages = Math.max(1, shifts.pageCount);
      if (newTotalPages !== totalPages) {
        setSearchParams(
          (prev) => {
            prev.set("totalPages", newTotalPages.toString());
            if (page > newTotalPages) {
              prev.set("page", "1");
            }
            return prev;
          },
          { replace: true },
        );
      }
    }
  }, [shifts?.pageCount, page, totalPages, setSearchParams]);

  const renderCell = useCallback(
    (
      item: GetLimeAdminShifts["response"]["shifts"][number],
      columnKey: React.Key,
    ) => {
      switch (columnKey) {
        case "date": {
          const date = new Date(item.start);
          const dateString = date.toLocaleDateString("sl-SI", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
          return <p className="text-sm">{dateString}</p>;
        }
        case "start": {
          const date = new Date(item.start);
          const startTime = date.toLocaleTimeString("sl-SI", {
            hour: "2-digit",
            minute: "2-digit",
          });
          return <p className="text-sm">{startTime}</p>;
        }
        case "end": {
          const date = new Date(item.end);
          const endTime = date.toLocaleTimeString("sl-SI", {
            hour: "2-digit",
            minute: "2-digit",
          });
          return <p className="text-sm">{endTime}</p>;
        }
        default:
          return null;
      }
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (selectedKeys === "all") {
      if (!selectedUserId || !selectedLocationId) {
        return;
      }

      await deleteShifts({
        type: "all",
        clientId,
        userId: selectedUserId,
        locationId: selectedLocationId,
      });
    } else if (selectedKeys.size > 0) {
      await deleteShifts({
        type: "byId",
        shiftIds: Array.from(selectedKeys).map((key) => Number(key)),
      });
    }

    addToast({
      title: t`Shifts successfully deleted`,
      color: "success",
    });

    setSelectedKeys(new Set());
    refetch();
  }, [
    selectedKeys,
    deleteShifts,
    selectedUserId,
    selectedLocationId,
    clientId,
    refetch,
  ]);

  return (
    <>
      <div>
        <div className="mt-4 flex gap-4 px-4">
          <LimeSelect
            label={t`Location`}
            items={
              locationsData?.locations.map((l) => ({
                label: l.label,
                key: l.locationId.toString(),
              })) ?? []
            }
            value={selectedLocationId?.toString()}
            onSingleItemChange={(value) => setSelectedLocationId(Number(value))}
            isLoading={isFetchingLocations}
          />

          <LimeSelect
            label={t`User`}
            items={
              usersData?.users.map((u) => ({
                label: u.name ?? "N/A",
                key: u.userId.toString(),
              })) ?? []
            }
            value={selectedUserId?.toString()}
            onSingleItemChange={(value) => setSelectedUserId(Number(value))}
            isLoading={isFetchingUsers}
          />
        </div>

        <Divider className="my-4" />

        <div className="px-2">
          <Table
            aria-label={t`Shifts`}
            selectedKeys={selectedKeys}
            selectionMode="multiple"
            onSelectionChange={setSelectedKeys}
            topContent={
              <div className="flex flex-row justify-end gap-2">
                <Button
                  variant="flat"
                  color="danger"
                  onPress={() => {
                    onOpen();
                  }}
                  isDisabled={
                    !selectedLocationId ||
                    !selectedUserId ||
                    !usersData ||
                    !locationsData
                  }
                  className="font-medium"
                  startContent={<Bomb size={16} />}
                >
                  BULK DELETE
                </Button>

                <AlertButton
                  buttonProps={{
                    variant: "flat",
                    color: "danger",
                    onPress: handleSubmit,
                    isDisabled:
                      selectedKeys !== "all" && selectedKeys.size === 0,
                    startContent: <Trash2 size={16} />,
                  }}
                  confirmProps={{
                    label: `${t`Izbriši izbrane izmene`} (${selectedKeys === "all" ? "vse" : selectedKeys.size})`,
                    onClick: handleSubmit,
                  }}
                  content={
                    selectedKeys === "all"
                      ? t`Izbriši vse izbrane izmene`
                      : t`Izbriši izbrane izmene`
                  }
                  title={t`Izbriši izbiro`}
                >
                  <Trans>Izbriši izbiro</Trans>
                </AlertButton>
              </div>
            }
            bottomContent={
              <LimePagination
                paginationProps={{
                  total: shifts?.pageCount ?? 0,
                  page,
                  onChange: (page) =>
                    setSearchParams((prev) => {
                      prev.set("page", page.toString());
                      return prev;
                    }),
                }}
                perPageProps={{
                  value: perPage.toString(),
                  values: [10, 20, 50, 100],
                  label: t`Shifts per page`,
                  onChange: (value) => {
                    setSearchParams((prev) => {
                      prev.set("perPage", value.toString());
                      prev.set("page", "1");
                      return prev;
                    });
                  },
                }}
              />
            }
          >
            <TableHeader>
              <TableColumn key="date">{t`Date`}</TableColumn>
              <TableColumn key="start">{t`Start`}</TableColumn>
              <TableColumn key="end">{t`End`}</TableColumn>
            </TableHeader>
            <TableBody
              items={shifts?.shifts ?? []}
              loadingContent={<Spinner />}
              emptyContent={t`No shifts found`}
              isLoading={isFetching}
            >
              {(item) => (
                <TableRow key={item.id}>
                  {(columnKey) => (
                    <TableCell>{renderCell(item, columnKey)}</TableCell>
                  )}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {selectedUserId && selectedLocationId && (
        <BulkDeleteModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          clientId={clientId}
          userId={selectedUserId}
          locationId={selectedLocationId}
          handleClose={() => {
            setSelectedKeys(new Set());
            refetch();
          }}
        />
      )}
    </>
  );
};

const BulkDeleteModal = ({
  isOpen,
  onOpenChange,
  clientId,
  userId,
  locationId,
  handleClose,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: number;
  userId: number;
  locationId: number;
  handleClose: () => void;
}) => {
  const [queryMessage, setQueryMessage] = useState<string | null>(null);

  const form = useForm<{
    dateRange: RangeValue<ZonedDateTime> | null;
    interval: "day" | "week" | "month" | "year" | "specific";
    specificDates: Date[];
    hasQueried: boolean;
  }>({
    initialValues: {
      dateRange: {
        start: parseAbsoluteToLocal(
          `${dayjs().format("YYYY-MM-DD")}T08:00:00Z`,
        ),
        end: parseAbsoluteToLocal(`${dayjs().format("YYYY-MM-DD")}T16:00:00Z`),
      },
      interval: "day",
      specificDates: [],
      hasQueried: false,
    },
  });

  const handleCloseMiddleware = useCallback(() => {
    form.reset();
    onOpenChange(false);
    handleClose();
    setQueryMessage(null);
  }, [form, onOpenChange, handleClose]);

  const { mutateAsync: deleteShifts, isPending: isDeleting } =
    api.admin.useDeleteShifts();

  const handleSubmit = useCallback(
    async (
      values: typeof form.values,
      e: FormEvent<HTMLFormElement> | undefined,
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isQuery = (e?.nativeEvent as any).submitter.value === "query";

      const { dateRange, hasQueried, ...rest } = values;

      if (!values.dateRange) {
        return;
      }

      const startDate = values.dateRange.start.toDate();
      const endDate = values.dateRange.end.toDate();

      const dateStart =
        startDate.getFullYear() +
        "-" +
        (startDate.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        startDate.getDate().toString().padStart(2, "0");
      const dateEnd =
        endDate.getFullYear() +
        "-" +
        (endDate.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        endDate.getDate().toString().padStart(2, "0");

      const shiftTimeStart = `${startDate?.getUTCHours().toString().padStart(2, "0")}:${startDate?.getUTCMinutes().toString().padStart(2, "0")}:00`;
      const shiftTimeEnd = `${endDate?.getUTCHours().toString().padStart(2, "0")}:${endDate?.getUTCMinutes().toString().padStart(2, "0")}:00`;

      const data = {
        ...rest,
        shiftTimeStart,
        shiftTimeEnd,
        dateStart,
        dateEnd,
      };

      const response = await deleteShifts({
        type: "repeatByShiftTime",
        ...data,
        clientId,
        userId,
        locationId,
        isQueryOnly: isQuery,
        specificDates: data.specificDates.map((d) =>
          dayjs(d).format("YYYY-MM-DD"),
        ),
      });

      if (isQuery) {
        setQueryMessage(
          `Najdene izmene: ${response?.shifts?.length ?? 0} (${dateStart} - ${dateEnd})`,
        );
      } else {
        handleCloseMiddleware();
        addToast({
          title: t`Izmene uspešno izbrisane`,
          color: "success",
        });
      }
    },
    [form, clientId, userId, locationId, deleteShifts, handleCloseMiddleware],
  );

  return (
    <LimeModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onClose={handleCloseMiddleware}
      size="4xl"
    >
      <ModalContent>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <ModalHeader>
            <Trans>Bulk delete</Trans>
          </ModalHeader>
          <ModalBody>
            <Tabs
              onSelectionChange={(key) => {
                if (key === "specific") {
                  form.setFieldValue("interval", "specific");
                } else {
                  form.setFieldValue("interval", "day");
                }
              }}
            >
              <Tab key={"range"} title={t`Razpon datumov`}>
                <div className="flex flex-col gap-2">
                  <LimeDateRangePicker
                    label={t`Datum in čas`}
                    {...form.getInputProps("dateRange")}
                  />

                  <LimeAlert
                    message={t`Datum je obseg v katerem se bodo iskale izmene, čas je pa začetni in končni čas izmen v obsegu`}
                    color="secondary"
                  />

                  <LimeSelect
                    label={t`Interval`}
                    items={[
                      { label: t`Dan`, key: "day" },
                      { label: t`Teden`, key: "week" },
                      { label: t`Mesec`, key: "month" },
                      { label: t`Leto`, key: "year" },
                    ]}
                    {...form.getInputProps("interval")}
                  />
                </div>
              </Tab>
              <Tab key={"specific"} title={t`Specifični dnevi`}>
                <div className="flex gap-2">
                  <Calendar
                    className="min-w-72"
                    mode="multiple"
                    selected={form.getValues().specificDates}
                    onSelect={(dates) => {
                      form.setFieldValue("specificDates", dates ?? []);
                    }}
                  />
                  <div className="flex flex-col gap-2">
                    {form.getValues().specificDates.map((d) => (
                      <p key={d.toISOString()}>{d.toISOString()}</p>
                    ))}
                  </div>
                </div>
              </Tab>
            </Tabs>

            <LimeAlert message={queryMessage} color="secondary" />
          </ModalBody>
          <ModalFooter>
            <Button
              color="default"
              variant="flat"
              onPress={() => {
                onOpenChange(false);
              }}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              color="primary"
              type="submit"
              variant="flat"
              isLoading={isDeleting}
              value="query"
              startContent={<Search size={16} />}
            >
              <Trans>Poizvedba</Trans>
            </Button>
            <Button
              color="danger"
              type="submit"
              variant="flat"
              isLoading={isDeleting}
              value="delete"
            >
              <Trans>Delete</Trans>
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </LimeModal>
  );
};
